import { Suspense, lazy, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from "@mui/material";
import DashboardLayout from "./layouts/DashboardLayout";
import PrivateRoute from "./components/Private/PrivateRoute";
import PublicRoute from "./components/PublicRoute/PublicRoute";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SignUp from "./sections/signup";
import CompanyPage from "./pages/CompanyPage";
import Verification from "./sections/verification";
import ResetPassword from "./sections/reset-password";

const CompanyVehicleDetailPage = lazy(
  () => import("./pages/CompanyVehiclesDetail"),
);

const PlanPage = lazy(() => import("./pages/PlanPage"));
const DiverPage = lazy(() => import("./pages/DiverPage"));
const DriverDetailPage = lazy(() => import("./pages/DriverDetailPage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));
const InvoiceViewPage = lazy(() => import("./pages/InvoiceViewPage"));
const CustomerPage = lazy(() => import("./pages/CustomerPage"));
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const EditOrderPage = lazy(() => import("./pages/EditOrderPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const LoginPage = lazy(() => import("./sections/login/LoginForm"));
const RiskAlertsPage = lazy(() => import("./pages/RiskAlertsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const OrderPage = lazy(() => import("./pages/OrderPage"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const CreateOrderPage = lazy(() => import("./pages/CreateOrderPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionViewPage = lazy(() => import("./pages/SubscriptionViewPage"));
const VehicleDetailPage = lazy(() => import("./pages/VehicleDetailPage"));
const VehicleMaintenancePage = lazy(
  () => import("./pages/VehicleMaintenancePage"),
);
const CompanyDetailPage = lazy(() => import("./pages/CompanyDetailPage"));
const SubscriptionManagementPage = lazy(
  () => import("./pages/SubscriptionManagementPage"),
);
const EnterprisePricingRequestsPage = lazy(
  () => import("./pages/EnterprisePricingRequestsPage"),
);
const SupplierDashboard = lazy(() => import("./pages/SupplierDashboard"));
const SupplierInvitesPage = lazy(() => import("./pages/SupplierInvitesPage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const UserPermissionsPage = lazy(() => import("./pages/UserPermissionsPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const OrdersChatPage = lazy(() => import("./pages/OrdersChatPage"));
const SupportChatPage = lazy(() => import("./pages/SupportChatPage"));
const GeneralSettingsPage = lazy(() => import("./pages/GeneralSettingsPage"));
const PushNotificationsPage = lazy(
  () => import("./pages/PushNotificationsPage"),
);
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const TrackShipmentPage = lazy(() => import("./pages/TrackShipmentPage"));

function LoadingFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
      }}
    >
      <CircularProgress color="primary" />
    </Box>
  );
}

function App() {
  const { i18n } = useTranslation();

  // Update HTML direction + lang on language change
  useEffect(() => {
    const updateDir = (lang) => {
      const isRTL = ["ar", "he", "fa", "ur"].includes(lang);
      document.dir = isRTL ? "rtl" : "ltr";
      document.documentElement.lang = lang;
    };

    updateDir(i18n.language);
    i18n.on("languageChanged", updateDir);

    return () => i18n.off("languageChanged", updateDir);
  }, [i18n]);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/track/:shipmentId" element={<TrackShipmentPage />} />
          <Route element={<PublicRoute />}>
            <Route path="/login-fleet" element={<LoginPage />} />
            <Route path="/admin-login" element={<LoginPage isAdmin={true} />} />
            <Route
              path="/login-supplier"
              element={<LoginPage isSupplier={true} />}
            />
            <Route
              path="/login"
              element={<Navigate to="/login-fleet" replace />}
            />
            <Route
              path="/signup"
              element={<Navigate to="/signup-fleet" replace />}
            />
            <Route
              path="/signup-fleet"
              element={<SignUp isSupplier={false} />}
            />
            <Route
              path="/signup-supplier"
              element={<SignUp isSupplier={true} />}
            />
            <Route path="/verify-code" element={<Verification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>
          <Route element={<PrivateRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route
                path="/subscription/:id"
                element={<SubscriptionViewPage />}
              />
              <Route index element={<DashboardPage />} />
              <Route path="/order" element={<OrderPage />} />
              <Route path="/order/:orderId" element={<OrderDetailPage />} />
              <Route path="/order/:orderId/edit" element={<EditOrderPage />} />
              <Route path="/driver" element={<DiverPage />} />
              <Route path="/driver/:id" element={<DriverDetailPage />} />
              <Route path="/vehicles" element={<VehiclesPage />} />
              <Route path="/product" element={<ProductPage />} />
              <Route path="/alerts" element={<RiskAlertsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/customer" element={<CustomerPage />} />
              <Route path="/customer/:id" element={<CustomerDetailPage />} />
              <Route path="/user" element={<UserManagementPage />} />
              <Route
                path="/user-permissions"
                element={<UserPermissionsPage />}
              />
              <Route path="/settings" element={<GeneralSettingsPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/orders" element={<OrdersChatPage />} />
              <Route path="/chat/support" element={<SupportChatPage />} />
              <Route path="/order/create" element={<CreateOrderPage />} />
              <Route
                path="/vehicles/:fleetId"
                element={<VehicleDetailPage />}
              />
              <Route
                path="/vehicles/:vehicleId/maintenance"
                element={<VehicleMaintenancePage />}
              />
              <Route path="/invoice" element={<InvoicePage />} />
              <Route path="/invoice/:id" element={<InvoiceViewPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/plans" element={<PlanPage />} />
              <Route
                path="/admin/enterprise-pricing"
                element={<EnterprisePricingRequestsPage />}
              />
              <Route
                path="/admin/subscription"
                element={<SubscriptionManagementPage />}
              />
              <Route
                path="/admin/company/:companyId"
                element={<CompanyDetailPage />}
              />
              <Route
                path="/admin/company/:companyId/vehicles/:fleetId"
                element={<CompanyVehicleDetailPage />}
              />
              <Route
                path="/admin/company/:companyId/customers/:id"
                element={<CustomerDetailPage />}
              />
              <Route
                path="/admin/company/:companyId/driver/:id"
                element={<DriverDetailPage />}
              />
              <Route path="/admin/company" element={<CompanyPage />} />
              <Route
                path="/supplier-dashboard"
                element={<SupplierDashboard />}
              />
              <Route
                path="/supplier-invites"
                element={<SupplierInvitesPage />}
              />
              <Route
                path="/notifications"
                element={<PushNotificationsPage />}
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
